import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingGroup, Spinner } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Heading } from "capitalrx-components-2.0/lib/typography";


import { useListMemberDependentsQuery } from "@/App/api/member";
import { ExperienceContext, MemberEligibilityContext } from "@/App/context";
import { AccumulatorCard } from "@/App/pages/Dashboard/Benefits/Accumulators/AccumulatorCard";
import { AccumulatorModal, AccumulatorModalProps } from "@/App/pages/Dashboard/Benefits/Accumulators/AccumulatorModal";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service";
import { AccumulationValues, Accumulators, DisplayNetworkStatuses, DisplayScopes, MutableAccumulationData } from "@/App/types/accumulation";

import "./CommercialBenefits.scss";

const categoryAccumlators: Record<DisplayScopes, Accumulators[]> = {
  individual: [Accumulators.PlanPayFertility],
  family: [],
};

export interface CommercialBenefitsProps {
  readonly displayNetworkStatus: DisplayNetworkStatuses;
  readonly accumulations: MutableAccumulationData;
  readonly hasMedical: boolean;
  readonly hasPharmacy: boolean;
  readonly isLoading: boolean;
}

interface AccumulationsSectionProps {
  readonly accumulations: MutableAccumulationData;
  readonly displayScope: DisplayScopes;
  readonly displayNetworkStatus: DisplayNetworkStatuses;
  readonly isFamilyCoverage?: boolean;
  readonly onOpen: (type: Accumulators, scope: DisplayScopes, accumulator: AccumulationValues) => void;
}

const AccumulationsSummary = (props: AccumulationsSectionProps) => {
  const {
    accumulations,
    displayNetworkStatus,
    displayScope,
    isFamilyCoverage,
  } = props;

  const { t: translate } = useTranslation();

  const { columns } = useResponsive({
    columns: {
      mobile: 12,
      tablet: 6,
      desktop: 4,
    },
  });

  const scopes = {
    individual: translate("BENEFITS.commercialBenefits.displayScopeIndividual"),
    family: translate("BENEFITS.commercialBenefits.displayScopeFamily"),
  };

  const accums = accumulations[displayNetworkStatus][displayScope];

  const medical: Partial<Record<Accumulators, AccumulationValues>> = {
    deductible: accums.deductible.find((accum) => accum.appliesTo.includes("medical")),
    outOfPocket: accums.outOfPocket.find((accum) => accum.appliesTo.includes("medical")),
    planPay: accums.planPay.find((accum) => accum.appliesTo.includes("medical")),
  };
  const pharmacy: Partial<Record<Accumulators, AccumulationValues>> = {
    deductible: accums.deductible.find((accum) => accum.appliesTo.includes("pharmacy")),
    outOfPocket: accums.outOfPocket.find((accum) => accum.appliesTo.includes("pharmacy")),
    planPay: accums.planPay.find((accum) => accum.appliesTo.includes("pharmacy")),
  };

  const hasMedical = !!(medical.deductible || medical.outOfPocket || medical.planPay);
  const hasPharmacy = !!(pharmacy.deductible || pharmacy.outOfPocket || pharmacy.planPay);

  const medicalTagFallback = (name: Accumulators): string[] => {
    if (hasPharmacy && !pharmacy[name]) {
      return ["medical", "pharmacy"];
    }
    return ["medical"];
  };

  const showMedicalTile = (name: Accumulators): boolean => {
    const hideNonEmbedded = DisplayScopes.Individual && isFamilyCoverage;
    const isEmbedded = medical[name]?.isEmbedded;

    if (hideNonEmbedded && !isEmbedded) {
      return false;
    }
    return hasMedical;
  };

  const showPharmacyTile = (name: Accumulators): boolean => {
    // If is unified and data is not combined or it's not unified
    const isUnified = hasPharmacy && hasMedical;
    const notCombined = pharmacy[name] !== medical[name];
    const inNetwork = displayNetworkStatus === DisplayNetworkStatuses.In;
    const isEmbedded = pharmacy[name]?.isEmbedded;

    if (!isEmbedded && isFamilyCoverage) {
      return false;
    }

    return inNetwork && ((isUnified && notCombined) || !isUnified);
  };

  let hasAny = false;
  for (const accumName of [Accumulators.Deductible, Accumulators.OutOfPocket, Accumulators.PlanPay]) {
    hasAny = hasAny || showMedicalTile(accumName) || showPharmacyTile(accumName);
  }

  return (
    <>
      { hasAny && (
        <Stack spacing="medium">
          <Heading>
            {scopes[displayScope]}: {translate("BENEFITS.commercialBenefits.summary")}
          </Heading>
          <Grid spacing="medium">
            {showMedicalTile(Accumulators.Deductible) && (
              <Grid.Span columns={columns}>
                <AccumulatorCard
                  displayScope={displayScope}
                  name={Accumulators.Deductible}
                  handleOpenModal={props.onOpen}
                  accumulator={medical.deductible}
                  tags={medical.deductible?.appliesTo ?? medicalTagFallback(Accumulators.Deductible)}
                  planHasAccumulator={!!medical.deductible}
                />
              </Grid.Span>
            )}
            {showMedicalTile(Accumulators.OutOfPocket) && (
              <Grid.Span columns={columns}>
                <AccumulatorCard
                  displayScope={displayScope}
                  name={Accumulators.OutOfPocket}
                  handleOpenModal={props.onOpen}
                  accumulator={medical.outOfPocket}
                  tags={medical.outOfPocket?.appliesTo ?? medicalTagFallback(Accumulators.OutOfPocket)}
                  planHasAccumulator={!!medical.outOfPocket}
                />
              </Grid.Span>
            )}
            {showMedicalTile(Accumulators.PlanPay) && (
              <Grid.Span columns={columns}>
                <AccumulatorCard
                  displayScope={displayScope}
                  name={Accumulators.PlanPay}
                  handleOpenModal={props.onOpen}
                  accumulator={medical.planPay}
                  tags={medical.planPay?.appliesTo ?? medicalTagFallback(Accumulators.PlanPay)}
                  planHasAccumulator={!!medical.planPay}
                />
              </Grid.Span>
            )}
            {hasMedical && hasPharmacy && <Grid.Break />}
            {showPharmacyTile(Accumulators.Deductible) && (
              <Grid.Span columns={columns}>
                <AccumulatorCard
                  displayScope={displayScope}
                  name={Accumulators.Deductible}
                  handleOpenModal={props.onOpen}
                  accumulator={pharmacy.deductible}
                  tags={pharmacy.deductible?.appliesTo ?? ["pharmacy"]}
                  planHasAccumulator={!!pharmacy.deductible}
                />
              </Grid.Span>
            )}
            {showPharmacyTile(Accumulators.OutOfPocket) && (
              <Grid.Span columns={columns}>
                <AccumulatorCard
                  displayScope={displayScope}
                  name={Accumulators.OutOfPocket}
                  handleOpenModal={props.onOpen}
                  accumulator={pharmacy.outOfPocket}
                  tags={pharmacy.outOfPocket?.appliesTo ?? ["pharmacy"]}
                  planHasAccumulator={!!pharmacy.outOfPocket}
                />
              </Grid.Span>
            )}
            {showPharmacyTile(Accumulators.PlanPay) && (
              <Grid.Span columns={columns}>
                <AccumulatorCard
                  displayScope={displayScope}
                  name={Accumulators.PlanPay}
                  handleOpenModal={props.onOpen}
                  accumulator={pharmacy.planPay}
                  tags={pharmacy.planPay?.appliesTo ?? ["pharmacy"]}
                  planHasAccumulator={!!pharmacy.planPay}
                />
              </Grid.Span>
            )}
          </Grid>
        </Stack>
      )}
    </>
  );
};

const AccumulationCategories = (props: AccumulationsSectionProps) => {
  const { accumulations, displayNetworkStatus, displayScope } = props;

  const { t: translate } = useTranslation();

  const { columns } = useResponsive({
    columns: {
      mobile: 12,
      tablet: 6,
      desktop: 4,
    },
  });

  const scopes = {
    individual: translate("BENEFITS.commercialBenefits.displayScopeIndividual"),
    family: translate("BENEFITS.commercialBenefits.displayScopeFamily"),
  };

  const categoryAccumNames = categoryAccumlators[displayScope];
  const categoryAccums: Array<[Accumulators, AccumulationValues]> = [];

  for (const accumName of categoryAccumNames) {
    const accums = accumulations[displayNetworkStatus][displayScope][accumName] ?? [];
    categoryAccums.push(...accums.map((accum): [Accumulators, AccumulationValues] => [accumName, accum]));
  }

  return (
    <>
      {categoryAccums.length > 0 && (
        <Stack spacing="medium">
          <Heading>
            {scopes[displayScope]}: {translate("BENEFITS.commercialBenefits.byCategory")}
          </Heading>
          <Grid spacing="medium">
            {categoryAccums.map(([accumName, accum], index) => (
              <Grid.Span columns={columns}>
                <AccumulatorCard
                  displayScope={displayScope}
                  name={accumName}
                  handleOpenModal={props.onOpen}
                  accumulator={accum}
                  tags={accum.appliesTo}
                  planHasAccumulator={true}
                  key={index}
                />
              </Grid.Span>
            ))}
          </Grid>
        </Stack>
      )}
    </>
  );
};

export const CommercialBenefits = (props: CommercialBenefitsProps) => {
  const { displayNetworkStatus, accumulations, isLoading } = props;

  const [modalData, setModalData] = useState<Omit<AccumulatorModalProps, "isVisible" | "onClose">>(undefined);

  const { hideAccumulators } = useContext(ExperienceContext);
  const { memberEligibility } = useContext(MemberEligibilityContext);

  const { data: dependents } = useListMemberDependentsQuery();

  const hasDependents = useMemo(() => {
    return (dependents?.results ?? []).length > 0;
  }, [dependents]);

  const handleOpenModal = useCallback(
    (type: Accumulators, scope: DisplayScopes, accumulator: AccumulationValues) => {
      if (type === Accumulators.Deductible) {
        logEvent(ANALYTIC_EVENTS.MY_RX.ACCUMULATOR.deductible.viewed);
      } else if (type === Accumulators.OutOfPocket) {
        logEvent(ANALYTIC_EVENTS.MY_RX.ACCUMULATOR.outOfPocket.viewed);
      } else if (type === Accumulators.PlanPay) {
        logEvent(ANALYTIC_EVENTS.MY_RX.ACCUMULATOR.planSpend.viewed);
      }
      setModalData({
        accumulatorType: type,
        displayScope: scope,
        displayNetworkStatus: displayNetworkStatus,
        balances: accumulator,
      });
    },
    [displayNetworkStatus, accumulations],
  );

  const handleCloseModal = useCallback(() => {
    setModalData(undefined);
  }, []);

  let isFamilyCoverage = false;
  if (memberEligibility?.member?.coverage_type) {
    isFamilyCoverage = !["01", "06", "08"].includes(memberEligibility.member.coverage_type);
  } else {
    isFamilyCoverage = hasDependents;
  }

  return (
    <LoadingGroup loading={isLoading}>
      <LoadingGroup.Placeholder
        fallback={
          <Shelf>
            <Shelf.Spacer />
            <Spinner size="large" />
            <Shelf.Spacer />
          </Shelf>
        }
      >
        <AccumulatorModal onClose={handleCloseModal} isVisible={!!modalData} {...modalData} />
        {!hideAccumulators && (
          <Stack spacing="large">
            <AccumulationsSummary
              accumulations={accumulations}
              displayNetworkStatus={displayNetworkStatus}
              displayScope={DisplayScopes.Individual}
              isFamilyCoverage={isFamilyCoverage}
              onOpen={handleOpenModal}
            />
            <AccumulationCategories
              accumulations={accumulations}
              displayNetworkStatus={displayNetworkStatus}
              displayScope={DisplayScopes.Individual}
              onOpen={handleOpenModal}
            />

            {isFamilyCoverage && (
              <AccumulationsSummary
                accumulations={accumulations}
                displayNetworkStatus={displayNetworkStatus}
                displayScope={DisplayScopes.Family}
                onOpen={handleOpenModal}
              />
            )}
            {isFamilyCoverage && (
              <AccumulationCategories
                accumulations={accumulations}
                displayNetworkStatus={displayNetworkStatus}
                displayScope={DisplayScopes.Family}
                onOpen={handleOpenModal}
              />
            )}
          </Stack>
        )}
      </LoadingGroup.Placeholder>
    </LoadingGroup>
  );
};
